
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { TeamMember } from '@/data/models/TeamMember';
import useRequest from '@/composables/useRequest';
import { CancelPendingInviteRequest } from '@/data/requests/CancelPendingInviteRequest';
import { RemoveMemberFromTeamRequest } from '@/data/requests/RemoveMemberFromTeamRequest';
import { popModal } from 'jenesius-vue-modal';
import { InviteUserRequest } from '@/data/requests/InviteUserRequest';
import ButtonElement from '@/components/ButtonElement.vue';
import useEventStatus from '@/composables/useEventStatus';

export default defineComponent({
  components: { ButtonElement },
  props: {
    member: {
      type: Object as () => TeamMember,
      required: true,
    },

    isEditing: {
      type: Boolean,
      default: false,
    },

    editErrorKickMember: {
      type: Function,
    },

    inviteOnClick: {
      type: Boolean,
      default: false,
    },

    editErrorInviteUser: {
      type: Function,
    },

    isCaptain: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['onRemove', 'onInvite'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const { isLive } = useEventStatus();

    let memberItemChipClass = 'accepted';
    let chipText = 'accepted';

    const loadingInviteUser = ref<boolean>();

    if (props.isCaptain) {
      memberItemChipClass = 'captain';
      chipText = t('modals.team.team_captain');
    }

    if (props.member.is_pending) {
      memberItemChipClass = 'pending-invite';
      chipText = t('modals.team.pending');
    }

    if (props.inviteOnClick) {
      memberItemChipClass = '';
      chipText = '';
    }

    const { loading: loadingKickMember, execute: executeKickMember } = useRequest<RemoveMemberFromTeamRequest, unknown>(
      new RemoveMemberFromTeamRequest(props.member.user_id),
      t('errors.team.cannot_kick')
    );

    const { loading: loadingCancelPendingInvite, execute: executeCancelPendingInvite } = useRequest<CancelPendingInviteRequest, unknown>(
      new CancelPendingInviteRequest(props.member.invite_id),
      t('errors.team.cannot_kick_pending')
    );

    const kickMemberFromTeam = () => {
      if (props.member.is_pending) {
        if (props.editErrorKickMember !== undefined) props.editErrorKickMember(undefined);

        executeCancelPendingInvite()
          .then(() => {
            emit('onRemove');
          })
          .catch(() => {
            if (props.editErrorKickMember !== undefined) props.editErrorKickMember(t('errors.team.cannot_kick'));
          });
      } else {
        if (props.editErrorKickMember !== undefined) props.editErrorKickMember(undefined);

        executeKickMember()
          .then(() => {
            emit('onRemove');
          })
          .catch(() => {
            if (props.editErrorKickMember !== undefined) props.editErrorKickMember(t('errors.team.cannot_kick'));
          });
      }
    };

    const { execute: executeInviteUser } = useRequest<InviteUserRequest, unknown>(new InviteUserRequest(props.member.user_id), '');

    const doInvite = () => {
      if (props.inviteOnClick === true) {
        if (props.editErrorInviteUser) props.editErrorInviteUser(undefined);

        loadingInviteUser.value = true;

        executeInviteUser()
          .then(async () => {
            emit('onInvite');

            popModal();
          })
          .catch(() => {
            loadingInviteUser.value = false;
            if (props.editErrorInviteUser) props.editErrorInviteUser(t('errors.invite.cannot_invite'));
          });
      }
    };

    return {
      props,
      memberItemChipClass,
      chipText,
      kickMemberFromTeam,
      loadingCancelPendingInvite,
      loadingKickMember,
      doInvite,
      loadingInviteUser,
      isLive,
    };
  },
});
